// Traduce los parámetros crudos de la categoría CONTACTENOS del backend
// (arreglo de { nombreParametro, valorParametro, descripcion, estado }) a la
// forma de datos que ya entiende Footer.js / footerDatosPorPais.js: web,
// servicioCliente (teléfonos, correo) y redesSociales.
//
// Cualquier "nombreParametro" que el backend agregue y no esté mapeado aquí
// se ignora sin romper el render — así el backend puede sumar parámetros
// nuevos sin requerir un deploy del frontend para tolerarlos.

const COTIZAR_URL_POR_DEFECTO = "https://www.somit.com/";

const NOMBRE_VISIBLE_RED = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
};

// El backend usa "N/A" como placeholder cuando un dato no aplica para esa
// aseguradora/país (ej. Whatsap="N/A") — nunca debe traducirse a un link roto.
function tieneValorReal(valor) {
  if (!valor) return false;
  const normalizado = valor.trim().toUpperCase();
  return normalizado !== "" && normalizado !== "N/A" && normalizado !== "NA";
}

function limpiarNumeroWhatsapp(valor) {
  return (valor || "").replace(/[^\d]/g, "");
}

function telefonoDesde(parametro) {
  return {
    etiqueta: "",
    numero: (parametro.descripcion || parametro.valorParametro || "").trim(),
    tel: (parametro.valorParametro || "").replace(/\s+/g, ""),
  };
}

function redSocialDesde(tipo, parametro, usarDescripcionComoEtiqueta) {
  return {
    tipo,
    usuario: usarDescripcionComoEtiqueta
      ? (parametro.descripcion || "").trim()
      : (NOMBRE_VISIBLE_RED[tipo] || tipo),
    url: tipo === "whatsapp"
      ? `https://wa.me/${limpiarNumeroWhatsapp(parametro.valorParametro)}`
      : parametro.valorParametro,
  };
}

const MAPEO_POR_NOMBRE_PARAMETRO = {
  UrlPaginaSomit: (p, acc) => {
    acc.web = { url: p.valorParametro, texto: "www.somit.com" };
  },
  CorreoContactSomit: (p, acc) => {
    acc.correoDireccion = p.valorParametro;
  },
  SubjCorreoContactSomit: (p, acc) => {
    acc.correoAsunto = p.valorParametro;
  },
  NumeroCabinaSomit: (p, acc) => acc.telefonos.push(telefonoDesde(p)),
  NumeroEmergenciaGM: (p, acc) => acc.telefonos.push(telefonoDesde(p)),
  RP: (p, acc) => acc.telefonos.push(telefonoDesde(p)),
  Instagram: (p, acc) => acc.redesSociales.push(redSocialDesde("instagram", p, false)),
  Facebook: (p, acc) => acc.redesSociales.push(redSocialDesde("facebook", p, false)),
  LinkedIn: (p, acc) => acc.redesSociales.push(redSocialDesde("linkedin", p, false)),
  Whatsap: (p, acc) => acc.redesSociales.push(redSocialDesde("whatsapp", p, true)),
  WhatsappGM: (p, acc) => acc.redesSociales.push(redSocialDesde("whatsapp", p, true)),
};

// Construye únicamente las claves para las que el backend realmente envió
// contenido válido. Un objeto disperso (sin la clave) preserva el fallback
// estático de footerDatosPorPais.js al mezclarse con spread:
// { ...datosEstaticos, ...datosApi }.
//
// "incluirServicioCliente" distingue dos escenarios que usan el MISMO
// endpoint pero deben mostrar distinto:
//   - Visitante anónimo (sin sesión): la consulta usa el código de
//     aseguradora público/fijo (ver footerDatosPorPais.js) solo para traer
//     lo que es igual para cualquier aseguradora — web y redes sociales.
//     Aunque ese código público ya tenga cabina/correo cargados en el
//     backend, NO deben mostrarse todavía: el visitante no ha confirmado
//     con qué aseguradora tiene póliza.
//   - Sesión autenticada con póliza seleccionada (AseguradoraContext trae
//     un código real): ahí sí se arma "servicioCliente" completo, porque
//     ya se sabe con certeza a qué aseguradora pertenece.
export function construirDatosFooterDesdeContactenos(parametros, opciones = {}) {
  const { incluirServicioCliente = false } = opciones;
  const acumulado = { telefonos: [], redesSociales: [] };

  parametros
    .filter((p) => p && p.nombreParametro && p.estado !== 0 && tieneValorReal(p.valorParametro))
    .forEach((p) => {
      const mapear = MAPEO_POR_NOMBRE_PARAMETRO[p.nombreParametro];
      if (mapear) mapear(p, acumulado);
    });

  const datos = {};

  if (acumulado.web) {
    datos.web = acumulado.web;
  }

  if (acumulado.redesSociales.length > 0) {
    datos.redesSociales = acumulado.redesSociales;
  }

  if (incluirServicioCliente && (acumulado.telefonos.length > 0 || acumulado.correoDireccion)) {
    datos.servicioCliente = {
      web: acumulado.web,
      telefonos: acumulado.telefonos,
      correo: acumulado.correoDireccion
        ? { direccion: acumulado.correoDireccion, asunto: acumulado.correoAsunto }
        : undefined,
      cotizar: { url: COTIZAR_URL_POR_DEFECTO },
    };
  }

  return datos;
}
